import { useQuery } from '@tanstack/react-query';

import { listCatalogApprovals } from './catalog-approvals.api';

export const CATALOG_APPROVALS_QK = {
  /**
   * Deliberately nested under the same `['catalog-approvals']` root the page's
   * list query uses, so the approve / reject mutations - which already
   * invalidate that root - refresh this badge too. A separate root would have
   * left the sidebar count stale until the next poll after every decision.
   */
  pendingCount: () => ['catalog-approvals', 'pending-count'] as const,
};

/**
 * 2026-07-23. Count for the sidebar "Product approvals" badge, mirroring the
 * lead-approvals badge next to it.
 *
 * `pageSize: 1` because only `pagination.totalItems` is wanted. The server
 * computes that from the whole filtered set before slicing
 * (`catalog-approvals.service.ts` - `totalItems = filtered.length`), so the
 * count is the full pending queue, not the page. `type: 'ALL'` keeps it
 * across Activities / Transfers / Hotels / Meals / Visas, matching what the
 * page opens on.
 *
 * 60s refetch matches the lead-approvals badge so the two counts in the same
 * sidebar group never drift visibly apart. `enabled` is driven by whether the
 * nav item is visible at all, so roles without the page never fire it.
 */
export const useCatalogApprovalsPendingCount = (enabled = true) =>
  useQuery({
    queryKey: CATALOG_APPROVALS_QK.pendingCount(),
    queryFn: () => listCatalogApprovals({ type: 'ALL', page: 1, pageSize: 1 }),
    enabled,
    refetchInterval: 60_000,
    // A badge is decoration - a failing count must never surface an error
    // boundary or a toast, it just stays at its last value.
    retry: false,
    throwOnError: false,
  });
